import React, { useState, useEffect, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { APP_CONFIG } from "../constants";
import ImageUpload from "./ImageUpload";
import { useFormData, useSearchableMakes, useSearchableModels } from "../hooks";
import SearchableSelect from "./SearchableSelect";
import DiagnosticResultView from "./DiagnosticResultView";
import Logo from '../assets/Logo.jpg';

// Step components defined outside to prevent re-creation
const Step1 = memo(({ formData, updateFormData, emailError, validateEmail }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-2">Tell Us About You</h2>
      <p className="text-gray-600">We need some basic information to get started</p>
    </div>

    <div className="space-y-4">
      {/* Name Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Full Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value)}
          placeholder="Enter your full name"
          className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
          required
        />
      </div>

      {/* Phone Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Phone Number <span className="text-red-500">*</span>
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => updateFormData('phone', e.target.value)}
          placeholder="Enter your phone number"
          className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
          required
        />
      </div>

      {/* Email Field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Email Address <span className="text-gray-400">(Optional)</span>
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => {
            updateFormData('email', e.target.value);
            validateEmail(e.target.value);
          }}
          onBlur={(e) => validateEmail(e.target.value)}
          placeholder="Enter your email address"
          className={`w-full p-3 border rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:ring-4 bg-white shadow-sm ${
            emailError ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
          }`}
        />
        {emailError && (
          <p className="mt-1 text-sm text-red-600">{emailError}</p>
        )}
      </div>
    </div>
  </div>
));

const Step2 = memo(({ formData, updateFormData, allMakes, filteredModels, makesLoading, makesError, modelsLoading, modelsError, modelSearchTerm, setModelSearchTerm, canEditYear }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-2">Vehicle Information</h2>
      <p className="text-gray-600">Tell us about your vehicle</p>
    </div>

    <div className="space-y-4">
      {/* Vehicle Make */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vehicle Make <span className="text-red-500">*</span>
        </label>
        <SearchableSelect
          options={allMakes}
          value={formData.vehicleMake}
          onChange={(value) => updateFormData('vehicleMake', value)}
          placeholder="Select vehicle make"
          loading={makesLoading}
          error={makesError}
          className="w-full"
        />
      </div>

      {/* Vehicle Model */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vehicle Model <span className="text-red-500">*</span>
        </label>
        <SearchableSelect
          options={filteredModels}
          value={formData.vehicleModel}
          onChange={(value) => updateFormData('vehicleModel', value)}
          placeholder="Select vehicle model"
          loading={modelsLoading}
          error={modelsError}
          className="w-full"
          showSearchBar={true}
          searchTerm={modelSearchTerm}
          onSearchChange={setModelSearchTerm}
        />
      </div>

      {/* Vehicle Year */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Vehicle Year <span className="text-red-500">*</span>
        </label>
        <input
          type="number"
          value={formData.vehicleYear}
          onChange={(e) => updateFormData('vehicleYear', e.target.value)}
          placeholder="Enter vehicle year"
          min="1900"
          max={new Date().getFullYear() + 1}
          className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm"
          required
          disabled={!canEditYear}
        />
        <p className="mt-1 text-sm text-gray-500">Enter the year of your vehicle</p>
      </div>
    </div>
  </div>
));

const Step3 = memo(({ formData, updateFormData }) => (
  <div className="space-y-6">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-2">Photos & Description</h2>
      <p className="text-gray-600">Upload photos and describe the issue</p>
    </div>

    <div className="space-y-6">
      {/* Dashboard Photo */}
      <ImageUpload
        title="Dashboard Photo"
        description="Upload a photo of your vehicle's dashboard showing warning lights"
        inputId="dashboard-photo"
        required={true}
        onImageChange={(image) => updateFormData('dashboardImage', image)}
      />

      {/* VIN Photo */}
      <ImageUpload
        title="VIN Photo"
        description="Upload a photo of your vehicle's VIN number (usually on the dashboard or door frame)"
        inputId="vin-photo"
        required={false}
        onImageChange={(image) => updateFormData('vinImage', image)}
      />

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value)}
          placeholder="Describe the issue you're experiencing with your vehicle..."
          rows={4}
          className="w-full p-3 border border-gray-300 rounded-lg text-base font-sans transition-all duration-300 focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 bg-white shadow-sm resize-none"
          required
        />
      </div>
    </div>
  </div>
));

const DiagnosticForm = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    description: '',
    dashboardImage: null,
    vinImage: null
  });
  
  const [currentStep, setCurrentStep] = useState(1);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Hooks for vehicle data
  const { allMakes, makesLoading, makesError, getMakeIdByName } = useSearchableMakes();
  
  // Get the Make_ID for the selected make
  const selectedMakeId = getMakeIdByName(formData.vehicleMake);
  
  // Debug logs
  console.log('🔍 Make ID lookup:', {
    vehicleMake: formData.vehicleMake,
    selectedMakeId,
    allMakesLength: allMakes.length
  });
  
  const { 
    filteredModels, 
    modelsLoading, 
    modelsError, 
    modelSearchTerm, 
    setModelSearchTerm 
  } = useSearchableModels(selectedMakeId);

  // Update form data function
  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Navigation functions
  const nextStep = useCallback(() => {
    if (currentStep < 3) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Validation function
  const isStepValid = useCallback((step) => {
    switch (step) {
      case 1:
        return formData.name.trim() && formData.phone.trim() && !emailError;
      case 2:
        return formData.vehicleMake && formData.vehicleModel && formData.vehicleYear;
      case 3:
        return formData.description.trim() && formData.dashboardImage;
      default:
        return false;
    }
  }, [formData, emailError]);

  // Check if year can be edited
  const canEditYear = useCallback(() => {
    return true; // Always allow editing the year
  }, []);

  // Email validation
  const validateEmail = useCallback((email) => {
    if (!email) {
      setEmailError('');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  }, []);

  // Auto-fill year when model is selected
  useEffect(() => {
    if (formData.vehicleModel && formData.vehicleModel !== '' && !formData.vehicleYear) {
      // Try to extract year from model name (e.g., "Corolla 2020" -> "2020")
      const yearMatch = formData.vehicleModel.match(/\b(19|20)\d{2}\b/);
      if (yearMatch) {
        updateFormData('vehicleYear', yearMatch[0]);
      } else {
        // If no year found in model name, set a default recent year
        const currentYear = new Date().getFullYear();
        updateFormData('vehicleYear', currentYear.toString());
      }
    }
  }, [formData.vehicleModel, formData.vehicleYear, updateFormData]);

  // Analysis function
  const analyze = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { name, phone, email, vehicleMake, vehicleModel, vehicleYear, description, dashboardImage, vinImage } = formData;
      
      // Check if we have vehicle information
      const hasVehicleInfo = vehicleMake && vehicleModel && vehicleYear;
      
      // Create FormData
      const formDataToSend = new FormData();
      
      // Agregar imágenes solo si existen
      if (dashboardImage) {
        formDataToSend.append("dashboard", dashboardImage);
      }
      if (vinImage) {
        formDataToSend.append("vin", vinImage);
      }
      
      formDataToSend.append("user_description", description);
      formDataToSend.append("customer_name", name);
      formDataToSend.append("customer_email", email || "");
      formDataToSend.append("customer_phone_number", phone);
      formDataToSend.append("user_id", "react_frontend_user");
      
      // Agregar información del vehículo si está disponible
      if (hasVehicleInfo) {
        formDataToSend.append("vehicle_make", vehicleMake);
        formDataToSend.append("vehicle_model", vehicleModel);
        formDataToSend.append("vehicle_year", vehicleYear);
      }

      // Debug: Log what we're sending
      console.log('📤 Sending form data:');
      for (let [key, value] of formDataToSend.entries()) {
        if (value instanceof File) {
          console.log(`${key}: File(${value.name}, ${value.size} bytes)`);
        } else {
          console.log(`${key}: ${value}`);
        }
      }

      // Real API call to the backend
      const response = await fetch("https://api.azucarai.com/dash_ai", {
        method: "POST",
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('📥 Received response:', result);

      if (result.success && result.content) {
        setAnalysisResult(result.content);
        setShowResult(true);
      } else {
        throw new Error(result.message || 'Analysis failed');
      }
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [formData]);

  // Handle new analysis
  const handleNewAnalysis = useCallback(() => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      vehicleMake: '',
      vehicleModel: '',
      vehicleYear: '',
      description: '',
      dashboardImage: null,
      vinImage: null
    });
    setCurrentStep(1);
    setAnalysisResult(null);
    setShowResult(false);
    setError(null);
    setEmailError('');
  }, []);

  // Handle back to form
  const handleBackToForm = useCallback(() => {
    setShowResult(false);
  }, []);

  // Progress bar component
  const ProgressBar = memo(() => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Step {currentStep} of 3</span>
        <span className="text-sm text-gray-500">{Math.round((currentStep / 3) * 100)}% Complete</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${(currentStep / 3) * 100}%` }}
        />
      </div>
    </div>
  ));

  // Render current step - memoized
  const renderCurrentStep = useCallback(() => {
    switch (currentStep) {
      case 1:
        return <Step1 formData={formData} updateFormData={updateFormData} emailError={emailError} validateEmail={validateEmail} />;
      case 2:
        return <Step2 formData={formData} updateFormData={updateFormData} allMakes={allMakes} filteredModels={filteredModels} makesLoading={makesLoading} makesError={makesError} modelsLoading={modelsLoading} modelsError={modelsError} modelSearchTerm={modelSearchTerm} setModelSearchTerm={setModelSearchTerm} canEditYear={canEditYear()} />;
      case 3:
        return <Step3 formData={formData} updateFormData={updateFormData} />;
      default:
        return <Step1 formData={formData} updateFormData={updateFormData} emailError={emailError} validateEmail={validateEmail} />;
    }
  }, [currentStep, formData, updateFormData, emailError, validateEmail, allMakes, filteredModels, makesLoading, makesError, modelsLoading, modelsError, modelSearchTerm, setModelSearchTerm, canEditYear]);

  // Show result view if we have a result
  if (showResult && analysisResult) {
    return (
      <DiagnosticResultView
        result={analysisResult}
        onBackToForm={handleBackToForm}
      />
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
       

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
           {/* Header */}
        <div className="text-center mb-8">
          <img src={Logo} alt="Logo" className="h-20 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Vehicle Diagnostic</h1>
          <p className="text-gray-600">Get instant AI-powered analysis of your vehicle issues</p>
        </div>
          <ProgressBar />
          
          {renderCurrentStep()}

          {/* Error Message */}
          {error && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              ← Back
            </button>

            {currentStep < 3 ? (
              <button
                onClick={nextStep}
                disabled={!isStepValid(currentStep)}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  isStepValid(currentStep)
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue →
              </button>
            ) : (
              <button
                onClick={analyze}
                disabled={!isStepValid(currentStep) || loading}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-200 ${
                  isStepValid(currentStep) && !loading
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {loading ? 'Analyzing...' : 'Analyze Vehicle'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticForm;
